import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  FileTextIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  LuggageIcon,
  SettingsIcon,
} from "lucide-react";

import { ThemeToggle } from "@/components/ThemeToggle";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { getMe } from "@/lib/api";

interface AdminLayoutProps {
  children?: ReactNode;
}

const navItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboardIcon },
  { title: "Posts", url: "/dashboard/posts", icon: FileTextIcon },
  { title: "Travels", url: "/dashboard/travels", icon: LuggageIcon },
  { title: "Settings", url: "/dashboard/settings", icon: SettingsIcon },
];

export function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState<string | null>(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    navigate("/login");
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    getMe(token)
      .then((user) => setUsername(user.username))
      .catch(handleLogout);
  }, [navigate, handleLogout]);

  return (
    <div className="page-scroll-container">
      <SidebarProvider>
        <Sidebar variant="inset" collapsible="icon">
          <SidebarHeader className="px-4 py-3">
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarMenu>
                {navItems.map((item) => {
                  const isActive = location.pathname === item.url;
                  return (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={item.title}
                      >
                        <a href={item.url}>
                          <item.icon />
                          <span>{item.title}</span>
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter className="p-4">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={handleLogout}
                  tooltip="Logout"
                >
                  <LogOutIcon data-icon />
                  <span>Logout</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>
        <SidebarInset className="mx-auto max-w-5xl">
          <header className="flex h-12 items-center justify-between border-b px-4">
            <SidebarTrigger />
            <ThemeToggle />
          </header>
          <div className="flex-1 overflow-auto p-6">
            {children ?? <Outlet />}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}

export default AdminLayout;
